import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../../context/AuthContext';

type AdminSettingType = 'text' | 'textarea' | 'signature';

interface AdminSettingItem {
  id: number;
  key: string;
  systemCode: string | null;
  label: string;
  section: string;
  type: AdminSettingType;
  helperText: string | null;
  placeholder: string | null;
  rows: number | null;
  sortOrder: number;
  value: string | null;
}

interface SettingsResponse {
  items: AdminSettingItem[];
}

interface SettingFormState {
  key: string;
  label: string;
  section: string;
  type: AdminSettingType;
  helperText: string;
  placeholder: string;
  rows: string;
  sortOrder: string;
  value: string;
}

const EMPTY_FORM: SettingFormState = {
  key: '',
  label: '',
  section: '',
  type: 'text',
  helperText: '',
  placeholder: '',
  rows: '',
  sortOrder: '0',
  value: '',
};

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function buildFormState(item?: AdminSettingItem): SettingFormState {
  if (!item) return EMPTY_FORM;

  return {
    key: item.key,
    label: item.label,
    section: item.section,
    type: item.type,
    helperText: item.helperText ?? '',
    placeholder: item.placeholder ?? '',
    rows: item.rows?.toString() ?? '',
    sortOrder: item.sortOrder.toString(),
    value: item.value ?? '',
  };
}

export default function AdminSettings() {
  const { authFetch } = useAuth();
  const [items, setItems] = useState<AdminSettingItem[]>([]);
  const [signatureUrls, setSignatureUrls] = useState<Record<string, string | null>>({});
  const [loading, setLoading] = useState(true);
  const [editingValueId, setEditingValueId] = useState<number | null>(null);
  const [draftValue, setDraftValue] = useState('');
  const [savingValueId, setSavingValueId] = useState<number | null>(null);
  const [activeSignatureId, setActiveSignatureId] = useState<number | null>(null);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | null>(null);
  const [dialogItemId, setDialogItemId] = useState<number | null>(null);
  const [formState, setFormState] = useState<SettingFormState>(EMPTY_FORM);
  const [submittingDefinition, setSubmittingDefinition] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const signatureUrlsRef = useRef<Record<string, string | null>>({});

  useEffect(() => {
    signatureUrlsRef.current = signatureUrls;
  }, [signatureUrls]);

  useEffect(() => () => {
    Object.values(signatureUrlsRef.current).forEach((url) => {
      if (url) URL.revokeObjectURL(url);
    });
  }, []);

  const setSignatureUrl = useCallback((settingId: number, nextUrl: string | null) => {
    setSignatureUrls((prev) => {
      const key = settingId.toString();
      const currentUrl = prev[key];
      if (currentUrl && currentUrl !== nextUrl) {
        URL.revokeObjectURL(currentUrl);
      }
      return { ...prev, [key]: nextUrl };
    });
  }, []);

  const loadSignaturePreview = useCallback(async (settingId: number) => {
    try {
      const res = await authFetch(`/api/admin/settings/${settingId}/signature`);
      if (!res.ok) {
        setSignatureUrl(settingId, null);
        return;
      }

      const blob = await res.blob();
      setSignatureUrl(settingId, URL.createObjectURL(blob));
    } catch {
      setSignatureUrl(settingId, null);
    }
  }, [authFetch, setSignatureUrl]);

  const loadSettings = useCallback(async () => {
    try {
      const res = await authFetch('/api/admin/settings');
      if (!res.ok) {
        throw new Error('Failed to load settings.');
      }

      const data = await res.json() as SettingsResponse;
      const nextItems = data.items ?? [];
      setItems(nextItems);
      await Promise.all(
        nextItems
          .filter((item) => item.type === 'signature')
          .map((item) => loadSignaturePreview(item.id))
      );
    } catch (error) {
      console.error('Error loading settings:', error);
      setMessage({ type: 'error', text: getErrorMessage(error, 'Failed to load settings.') });
    } finally {
      setLoading(false);
    }
  }, [authFetch, loadSignaturePreview]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const openCreateDialog = () => {
    setDialogMode('create');
    setDialogItemId(null);
    setFormState(EMPTY_FORM);
    setMessage(null);
  };

  const openEditDialog = (item: AdminSettingItem) => {
    setDialogMode('edit');
    setDialogItemId(item.id);
    setFormState(buildFormState(item));
    setMessage(null);
  };

  const closeDialog = () => {
    setDialogMode(null);
    setDialogItemId(null);
    setFormState(EMPTY_FORM);
  };

  const startValueEditing = (item: AdminSettingItem) => {
    setEditingValueId(item.id);
    setDraftValue(item.value ?? '');
    setMessage(null);
  };

  const cancelValueEditing = () => {
    setEditingValueId(null);
    setDraftValue('');
  };

  const saveValue = async (item: AdminSettingItem) => {
    setSavingValueId(item.id);
    setMessage(null);

    try {
      const res = await authFetch(`/api/admin/settings/${item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: draftValue }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.message || `Failed to save ${item.label}.`);
      }

      const updated = data?.item as AdminSettingItem | undefined;
      if (updated) {
        setItems((prev) => prev.map((current) => (current.id === updated.id ? updated : current)));
      } else {
        await loadSettings();
      }

      setEditingValueId(null);
      setDraftValue('');
      setMessage({ type: 'success', text: `${item.label} saved.` });
    } catch (error) {
      console.error('Error saving value:', error);
      setMessage({ type: 'error', text: getErrorMessage(error, `Failed to save ${item.label}.`) });
    } finally {
      setSavingValueId(null);
    }
  };

  const handleDefinitionSubmit = async () => {
    setSubmittingDefinition(true);
    setMessage(null);

    const payload = {
      key: formState.key,
      label: formState.label,
      section: formState.section,
      type: formState.type,
      helperText: formState.helperText || null,
      placeholder: formState.type === 'signature' ? null : (formState.placeholder || null),
      rows: formState.type === 'textarea' && formState.rows !== '' ? Number(formState.rows) : null,
      sortOrder: formState.sortOrder === '' ? 0 : Number(formState.sortOrder),
      ...(dialogMode === 'create' && formState.type !== 'signature'
        ? { value: formState.value }
        : {}),
    };

    try {
      const url =
        dialogMode === 'create'
          ? '/api/admin/settings'
          : `/api/admin/settings/${dialogItemId}`;
      const method = dialogMode === 'create' ? 'POST' : 'PUT';

      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.message || 'Failed to save setting definition.');
      }

      closeDialog();
      await loadSettings();
      setMessage({
        type: 'success',
        text: dialogMode === 'create' ? 'Setting created.' : 'Setting updated.',
      });
    } catch (error) {
      console.error('Error saving definition:', error);
      setMessage({ type: 'error', text: getErrorMessage(error, 'Failed to save setting definition.') });
    } finally {
      setSubmittingDefinition(false);
    }
  };

  const handleDelete = async (item: AdminSettingItem) => {
    if (!confirm(`Delete "${item.label}"?`)) return;

    try {
      const res = await authFetch(`/api/admin/settings/${item.id}`, {
        method: 'DELETE',
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.message || `Failed to delete ${item.label}.`);
      }

      setItems((prev) => prev.filter((current) => current.id !== item.id));
      setSignatureUrl(item.id, null);
      if (editingValueId === item.id) {
        cancelValueEditing();
      }
      setMessage({ type: 'success', text: `${item.label} deleted.` });
    } catch (error) {
      console.error('Error deleting setting:', error);
      setMessage({ type: 'error', text: getErrorMessage(error, `Failed to delete ${item.label}.`) });
    }
  };

  const handleSignatureUpload = async (item: AdminSettingItem, file: File) => {
    const formData = new FormData();
    formData.append('signature', file);
    setActiveSignatureId(item.id);
    setMessage(null);

    try {
      const res = await authFetch(`/api/admin/settings/${item.id}/signature`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.message || `Failed to upload ${item.label}.`);
      }

      await loadSignaturePreview(item.id);
      setMessage({ type: 'success', text: `${item.label} uploaded.` });
    } catch (error) {
      console.error('Error uploading signature:', error);
      setMessage({ type: 'error', text: getErrorMessage(error, `Failed to upload ${item.label}.`) });
    } finally {
      setActiveSignatureId(null);
      const input = fileInputRefs.current[item.id.toString()];
      if (input) input.value = '';
    }
  };

  const handleSignatureDelete = async (item: AdminSettingItem) => {
    if (!confirm(`Remove "${item.label}"?`)) return;

    setActiveSignatureId(item.id);
    setMessage(null);

    try {
      const res = await authFetch(`/api/admin/settings/${item.id}/signature`, {
        method: 'DELETE',
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.message || `Failed to remove ${item.label}.`);
      }

      setSignatureUrl(item.id, null);
      setItems((prev) =>
        prev.map((current) => (current.id === item.id ? { ...current, value: null } : current))
      );
      setMessage({ type: 'success', text: `${item.label} removed.` });
    } catch (error) {
      console.error('Error deleting signature:', error);
      setMessage({ type: 'error', text: getErrorMessage(error, `Failed to remove ${item.label}.`) });
    } finally {
      setActiveSignatureId(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h5">Admin Settings</Typography>
            <Typography variant="body2" color="text.secondary">
              Dynamic settings definitions with inline values and signature assets.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
            Add Setting
          </Button>
        </Box>

        {message && (
          <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Section</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Label</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 180 }}>Key</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 110 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 360 }}>Value / Preview</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 250 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No settings found
                  </TableCell>
                </TableRow>
              )}

              {items.map((item) => {
                const isValueEditing = editingValueId === item.id;
                const isValueSaving = savingValueId === item.id;
                const isSignatureBusy = activeSignatureId === item.id;
                const previewUrl = signatureUrls[item.id.toString()];

                return (
                  <TableRow key={item.id} hover>
                    <TableCell>{item.section}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.label}
                      </Typography>
                      {item.helperText && (
                        <Typography variant="caption" color="text.secondary">
                          {item.helperText}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {item.key}
                      </Typography>
                      {item.systemCode && (
                        <Typography variant="caption" color="text.secondary">
                          {item.systemCode}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{item.type}</TableCell>
                    <TableCell>
                      {item.type === 'signature' ? (
                        previewUrl ? (
                          <Box
                            component="img"
                            src={previewUrl}
                            alt={item.label}
                            sx={{
                              display: 'block',
                              maxWidth: 180,
                              maxHeight: 72,
                              objectFit: 'contain',
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              p: 0.5,
                              bgcolor: 'background.paper',
                            }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No signature uploaded
                          </Typography>
                        )
                      ) : isValueEditing ? (
                        <TextField
                          value={draftValue}
                          onChange={(e) => setDraftValue(e.target.value)}
                          fullWidth
                          size="small"
                          multiline={item.type === 'textarea'}
                          minRows={item.type === 'textarea' ? item.rows ?? 3 : undefined}
                          placeholder={item.placeholder ?? undefined}
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                        >
                          {item.value || 'N/A'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.type === 'signature' ? (
                        <>
                          <input
                            type="file"
                            accept="image/png,image/jpeg"
                            ref={(node) => {
                              fileInputRefs.current[item.id.toString()] = node;
                            }}
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleSignatureUpload(item, file);
                            }}
                          />
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button size="small" onClick={() => openEditDialog(item)}>
                              Edit
                            </Button>
                            <Button
                              size="small"
                              onClick={() => fileInputRefs.current[item.id.toString()]?.click()}
                              disabled={isSignatureBusy}
                            >
                              {isSignatureBusy ? 'Working...' : 'Upload'}
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => handleSignatureDelete(item)}
                              disabled={!previewUrl || isSignatureBusy}
                            >
                              Remove
                            </Button>
                            <Button size="small" color="error" onClick={() => handleDelete(item)}>
                              Delete
                            </Button>
                          </Box>
                        </>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {isValueEditing ? (
                            <>
                              <Button
                                size="small"
                                color="success"
                                onClick={() => saveValue(item)}
                                disabled={isValueSaving}
                              >
                                {isValueSaving ? 'Saving...' : 'Save'}
                              </Button>
                              <Button size="small" onClick={cancelValueEditing} disabled={isValueSaving}>
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <Button size="small" onClick={() => startValueEditing(item)}>
                              Value
                            </Button>
                          )}
                          <Button size="small" onClick={() => openEditDialog(item)}>
                            Edit
                          </Button>
                          <Button size="small" color="error" onClick={() => handleDelete(item)}>
                            Delete
                          </Button>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <Dialog
        open={dialogMode !== null}
        onClose={() => !submittingDefinition && closeDialog()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{dialogMode === 'create' ? 'Add Setting' : 'Edit Setting'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Key"
            value={formState.key}
            onChange={(e) => setFormState((prev) => ({ ...prev, key: e.target.value }))}
            sx={{ mt: 1 }}
            helperText="Letters, numbers, and underscores only."
          />
          <TextField
            fullWidth
            label="Label"
            value={formState.label}
            onChange={(e) => setFormState((prev) => ({ ...prev, label: e.target.value }))}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label="Section"
            value={formState.section}
            onChange={(e) => setFormState((prev) => ({ ...prev, section: e.target.value }))}
            sx={{ mt: 2 }}
          />
          <TextField
            select
            fullWidth
            label="Type"
            value={formState.type}
            onChange={(e) =>
              setFormState((prev) => ({
                ...prev,
                type: e.target.value as AdminSettingType,
                rows: e.target.value === 'textarea' ? prev.rows || '3' : '',
                placeholder: e.target.value === 'signature' ? '' : prev.placeholder,
                value: e.target.value === 'signature' ? '' : prev.value,
              }))
            }
            sx={{ mt: 2 }}
          >
            <MenuItem value="text">text</MenuItem>
            <MenuItem value="textarea">textarea</MenuItem>
            <MenuItem value="signature">signature</MenuItem>
          </TextField>
          <TextField
            fullWidth
            label="Helper Text"
            value={formState.helperText}
            onChange={(e) => setFormState((prev) => ({ ...prev, helperText: e.target.value }))}
            sx={{ mt: 2 }}
          />
          {formState.type !== 'signature' && (
            <TextField
              fullWidth
              label="Placeholder"
              value={formState.placeholder}
              onChange={(e) => setFormState((prev) => ({ ...prev, placeholder: e.target.value }))}
              sx={{ mt: 2 }}
            />
          )}
          {formState.type === 'textarea' && (
            <TextField
              fullWidth
              type="number"
              label="Rows"
              value={formState.rows}
              onChange={(e) => setFormState((prev) => ({ ...prev, rows: e.target.value }))}
              sx={{ mt: 2 }}
            />
          )}
          <TextField
            fullWidth
            type="number"
            label="Sort Order"
            value={formState.sortOrder}
            onChange={(e) => setFormState((prev) => ({ ...prev, sortOrder: e.target.value }))}
            sx={{ mt: 2 }}
          />
          {dialogMode === 'create' && formState.type !== 'signature' && (
            <TextField
              fullWidth
              label="Initial Value"
              value={formState.value}
              onChange={(e) => setFormState((prev) => ({ ...prev, value: e.target.value }))}
              sx={{ mt: 2 }}
              multiline={formState.type === 'textarea'}
              minRows={formState.type === 'textarea' ? Number(formState.rows || '3') : undefined}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submittingDefinition}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleDefinitionSubmit} disabled={submittingDefinition}>
            {submittingDefinition ? 'Saving...' : dialogMode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
